'use client';

import React, { useState } from 'react';

import { Body } from '@depop/web-thrift-ui/Typography';
import { Modal } from '@depop/web-ui-kit/Modal';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import { FollowsButton } from '../FollowsButton';
import { FollowsModal } from '../FollowsModal';

import styles from './styles.module.css';

import { DownloadAppModal } from '@/components/DownloadAppModal/DownloadAppModal';
import {
  RQ_FOLLOWING_RELATIONSHIP,
  RQ_SHOP_USERNAME_KEY,
} from '@/modules/ReactQuery/cacheKeys';
import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { FollowRole } from '@/modules/follows/types';
import { useFollowingRelationship } from '@/modules/follows/useFollowingRelationship';
import { useFollows } from '@/modules/follows/useFollows';
import { useIsViewingOwnShop } from '@/modules/shop/hooks/useIsViewingOwnShop';
import { formatNumberWithMetricPrefix } from '@/modules/text/helpers';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

type Props = {
  following: number;
  followers: number;
  username: string;
  sellerId: number;
};

export function Follows({ sellerId, following, followers, username }: Props) {
  const [isAuthenticated, currentUser] = useCurrentUser();
  const userId = currentUser?.id;
  const t = useTranslations('shop');
  const [isModalOpen, setIsModalOpen] = useState<FollowRole | boolean>(false);
  const modalTab = isModalOpen === FollowRole.Followers ? 0 : 1;
  const isOwnShop = useIsViewingOwnShop();
  const { sendActivityTrackerEvent } = useActivityTracker({
    userId,
  });

  const { data: followingRelationshipData } = useFollowingRelationship({
    sellerId,
    username,
  });
  const isFollowing = !!followingRelationshipData?.data?.is_following;
  const { isUpdatingFollowCount, handleFollowsButtonClick } = useFollows({
    isFollowing,
    queriesToInvalidate: [
      [RQ_FOLLOWING_RELATIONSHIP, username],
      [RQ_SHOP_USERNAME_KEY, username],
    ],
  });

  const followButtons = [
    {
      followRole: FollowRole.Followers,
      value: formatNumberWithMetricPrefix(Math.max(0, followers)),
      description: t('Followers', { count: Math.max(0, followers) }),
    },
    {
      followRole: FollowRole.Following,
      value: formatNumberWithMetricPrefix(Math.max(0, following)),
      description: t('Following', { count: Math.max(0, following) }),
    },
  ];

  async function handleButtonClick() {
    if (!isAuthenticated) {
      setIsModalOpen(true);
      sendActivityTrackerEvent(
        ActivityTrackerEventType.USER_INTERACTION_ACTION,
        { interactionType: 'FollowIntent', userId: sellerId }
      );
    } else {
      await handleFollowsButtonClick(sellerId);
    }
  }

  function handleFollowCountClick(followRole: FollowRole) {
    setIsModalOpen(followRole);
  }

  return (
    <div className={styles.container}>
      {followButtons.map(({ followRole, description, value }) => (
        <button
          key={followRole}
          className={clsx(styles.followCount, {
            [styles.followCountOwnShop]: isOwnShop,
          })}
          aria-label={
            followRole === FollowRole.Followers
              ? t('ShowFollowers')
              : t('ShowFollowing')
          }
          aria-haspopup="dialog"
          onClick={() => handleFollowCountClick(followRole)}
        >
          <Body variant="sm-bold" as="span">
            {value}
          </Body>{' '}
          <Body variant="sm-regular" as="span">
            {description}
          </Body>
        </button>
      ))}
      {!isOwnShop && (
        <FollowsButton
          isFollowing={isFollowing}
          onButtonClick={handleButtonClick}
          isUpdatingFollowCount={isUpdatingFollowCount}
        />
      )}
      <Modal
        isOpen={!!isModalOpen}
        setOpen={setIsModalOpen}
        title={isAuthenticated ? `@${username}` : ''}
        content={() =>
          isAuthenticated ? (
            <FollowsModal
              sellerId={sellerId}
              isOwnShop={isOwnShop}
              shopUsername={username}
              defaultTab={modalTab}
            />
          ) : (
            <DownloadAppModal downloadAppTrackingId="follows_modal" />
          )
        }
        wide={isAuthenticated}
      />
    </div>
  );
}
